import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { apiClient } from '@api/client';
import type {
  OrgUnit,
  OrgUnitListResponse,
  CreateOrgUnitRequest,
  CreateOrgUnitResponse,
  UpdateOrgUnitRequest,
  OrgMember,
} from '@schemas/api';
import { useNotificationStore } from '@store/notificationStore';

const extractErrorMessage = (error: any): string => {
  if (error.response?.data?.detail) {
    const detail = error.response.data.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail) && detail.length > 0) {
      return detail[0].msg || 'An error occurred';
    }
  }
  return 'An error occurred';
};

export const useListOrgUnits = (hierarchyId: string | null) => {
  return useQuery({
    queryKey: ['orgHierarchies', hierarchyId, 'units'],
    queryFn: async () => {
      if (!hierarchyId) return [];
      const { data } = await apiClient.get<OrgUnitListResponse>(
        `/org/units?hierarchy_id=${hierarchyId}`
      );
      return data.items || [];
    },
    enabled: !!hierarchyId,
    staleTime: 1000 * 60 * 5,
  });
};

export const useAllOrgUnits = () => {
  return useQuery({
    queryKey: ['orgUnits', 'all'],
    queryFn: async () => {
      const { data } = await apiClient.get<OrgUnitListResponse>(
        '/org/units?skip=0'
      );
      return data.items || [];
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useGetOrgUnit = (unitId: string | null) => {
  return useQuery({
    queryKey: ['orgUnits', unitId],
    queryFn: async () => {
      if (!unitId) return null;
      const { data } = await apiClient.get<OrgUnit>(
        `/org/units/${unitId}`
      );
      return data;
    },
    enabled: !!unitId,
    staleTime: 1000 * 60 * 5,
  });
};

export const useCreateOrgUnit = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async (payload: CreateOrgUnitRequest) => {
      const { data } = await apiClient.post<CreateOrgUnitResponse>(
        '/org/units',
        payload
      );
      return data;
    },
    onSuccess: (data, variables) => {
      addNotification({
        message: `Unit "${data.name}" created successfully`,
        type: 'success',
      });
      // Invalidate units list for this hierarchy
      queryClient.invalidateQueries({
        queryKey: ['orgHierarchies', variables.hierarchy_id, 'units'],
      });
    },
    onError: (error: any) => {
      addNotification({
        message: extractErrorMessage(error) || 'Failed to create unit',
        type: 'error',
      });
    },
  });
};

export const useUpdateOrgUnit = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async (payload: {
      unitId: string;
      hierarchyId: string;
      data: UpdateOrgUnitRequest;
    }) => {
      const { data } = await apiClient.patch<OrgUnit>(
        `/org/units/${payload.unitId}`,
        payload.data
      );
      return data;
    },
    onSuccess: (_data) => {
      addNotification({
        message: 'Unit updated successfully',
        type: 'success',
      });
      // Invalidate all related queries to ensure UI updates
      queryClient.invalidateQueries({
        queryKey: ['orgUnits'],
      });
      queryClient.invalidateQueries({
        queryKey: ['orgHierarchies'],
      });
    },
    onError: (error: any) => {
      addNotification({
        message: extractErrorMessage(error) || 'Failed to update unit',
        type: 'error',
      });
    },
  });
};

export const useDeleteOrgUnit = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async (payload: { unitId: string; hierarchyId: string }) => {
      await apiClient.delete(`/org/units/${payload.unitId}`);
      return payload;
    },
    onSuccess: (data) => {
      addNotification({
        message: 'Unit deleted successfully',
        type: 'success',
      });
      queryClient.invalidateQueries({
        queryKey: ['orgHierarchies', data.hierarchyId, 'units'],
      });
      queryClient.invalidateQueries({
        queryKey: ['orgUnits', data.unitId],
      });
    },
    onError: (error: any) => {
      addNotification({
        message: extractErrorMessage(error) || 'Failed to delete unit',
        type: 'error',
      });
    },
  });
};

export const useListOrgUnitMembers = (unitId: string | null) => {
  return useQuery({
    queryKey: ['orgUnits', unitId, 'members'],
    queryFn: async () => {
      if (!unitId) return [];
      const { data } = await apiClient.get<{ items: OrgMember[] }>(
        `/org/units/${unitId}/members`
      );
      return data.items || [];
    },
    enabled: !!unitId,
    staleTime: 1000 * 60 * 5,
  });
};
